// Part-done spray task sign-off (mig 20261207000000).
//
// A grouped task (blocks 5A · 5B · 5C) can stop part-way. The Finish sheet then
// records ONLY the blocks sprayed and, optionally, asks the server to plan the
// rest (`remaining_block_ids` in the payload). This file holds the rules so the
// sheet and its tests share one copy; it does no I/O.

/** Shown when the spray saved but the server could not plan the rest. */
export const FOLLOWUP_FAILED_NOTE = 'The rest could not be planned. Ask a manager to plan it on the Plan tab.'
export const FOLLOWUP_FAILED_MESSAGE = `Saved. ${FOLLOWUP_FAILED_NOTE}`

/** Shown after an offline sign-off. */
export const FOLLOWUP_QUEUED_NOTE = 'The rest will be planned when it syncs.'

export interface PartDonePlan {
  /** Covered blocks that were ticked, in the task's own order. */
  sprayedIds:   string[]
  /** Covered blocks left unticked. */
  untickedIds:  string[]
  /** The record's primary block: the task's own block if ticked, else the first ticked. null = none ticked. */
  primaryId:    string | null
  /** True when the primary moved off the task's block (so the planting must not follow). */
  primaryMoved: boolean
  /** `block_ids` for the RPC: the sprayed set when 2 or more, else []. */
  blockIds:     string[]
  /** `remaining_block_ids` for the payload, or null when the rest is not being planned. */
  remainingIds: string[] | null
}

function taskBlockFirst(ids: string[], taskBlockId: string): string[] {
  return ids.includes(taskBlockId) ? [taskBlockId, ...ids.filter(id => id !== taskBlockId)] : ids
}

export function partDonePlan(args: {
  taskBlockId: string
  /** Every block the task covers (primary included). Empty = a plain single-block task. */
  coveredIds:  string[]
  /** Blocks ticked on the sheet. Ignored for a single-block task. */
  ticked:      ReadonlySet<string>
  planRest:    boolean
}): PartDonePlan {
  const covered = args.coveredIds.length > 0 ? args.coveredIds : [args.taskBlockId]
  const grouped = covered.length > 1
  const sprayedIds  = grouped ? covered.filter(id => args.ticked.has(id)) : covered
  const untickedIds = grouped ? covered.filter(id => !args.ticked.has(id)) : []
  const primaryId = sprayedIds.includes(args.taskBlockId) ? args.taskBlockId : (sprayedIds[0] ?? null)
  return {
    sprayedIds,
    untickedIds,
    primaryId,
    primaryMoved: primaryId != null && primaryId !== args.taskBlockId,
    blockIds:     sprayedIds.length >= 2 ? sprayedIds : [],
    // The task's own block first: the server makes the first id the follow-up's
    // primary, and only that block carries the planting.
    remainingIds: args.planRest ? taskBlockFirst(untickedIds.length > 0 ? untickedIds : sprayedIds, args.taskBlockId) : null,
  }
}

/** The sentence under the "plan the rest" tick. */
export function followupHint(blockNames: string[]): string {
  return blockNames.length > 0
    ? `A new planned task for ${blockNames.join(' · ')} will be added to the Plan tab.`
    : 'A new planned task will be added to the Plan tab.'
}
